import type { Log } from "@slot/presentation";
import type { AssetRegistry } from "../assets/AssetRegistry";
import type { SlotApp } from "../app/createSlotApp";
import type { GameBackgroundDefinition } from "../composition/GameDefinition";
import type { StageLayers } from "../boot/createStage";
import { SHELL_BUNDLE_NAMES } from "../boot/defaults/BundleNames";
import { DEFAULT_STILL_OVERSCAN, StillBackgroundElement, StillBackgroundView } from "./StillBackground";

/**
 * The title's still under its scene (decision 69), by the boot bundle's URLs: in the canvas (`draw` "canvas"), at the
 * bottom of the game layer, resolving once its first still is decoded; or under the transparent canvas (`draw` "dom"),
 * at the bottom of the background root. It lives as long as the app; null when the boot bundle has neither still.
 */
export async function mountStillBackground(input: {
  background: GameBackgroundDefinition;
  assets: AssetRegistry;
  slot: SlotApp;
  layers: StageLayers;
  log: Log;
}): Promise<StillBackgroundView | StillBackgroundElement | null> {
  const { background, assets, slot, layers, log } = input;
  const url = (path: string): string | null => assets.resolveUrl(SHELL_BUNDLE_NAMES.bootAssets, path);
  const urls = { portrait: url(background.stills.portrait), landscape: url(background.stills.landscape) };
  if (!urls.portrait && !urls.landscape) {
    log(`[StillBackground] neither "${background.stills.portrait}" nor "${background.stills.landscape}" is in the boot bundle: no background`);
    return null;
  }
  const overscan = background.overscan ?? DEFAULT_STILL_OVERSCAN;
  if (background.draw === "dom") {
    return slot.backgroundRoot ? new StillBackgroundElement({ root: slot.backgroundRoot, urls, overscan, viewport: slot.viewport }) : null;
  }
  const view = new StillBackgroundView({ urls, overscan, viewport: slot.viewport, log });
  layers.game.addChildAt(view, 0);
  await view.ready;
  return view;
}
